import React, { useState } from 'react';
import Layout from '../components/Layout';
import { readResponseJSON, readResponse } from '../utils/fetchSafe';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Trash2, Users, FileSearch, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hits the admin dedupe endpoints. All three actions follow the same
// pattern: kick off a request, render the JSON result in a collapsible
// preview block, surface a toast on success/failure. We never apply
// destructive operations without an explicit second click.
const callApi = async (method, path) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Authorization': `Bearer ${token}` },
  });
  const { data, ok, status } = await readResponse(res);
  if (!ok) throw new Error(data?.detail || `HTTP ${status}`);
  return data;
};

const ResultPanel = ({ title, data }) => {
  if (!data) return null;
  return (
    <div className="mt-4 border border-gray-200 rounded p-3 bg-gray-50 max-h-[400px] overflow-auto">
      <div className="text-xs text-gray-500 mb-1">{title}</div>
      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
};

const DedupTools = () => {
  // Each section keeps its own loading + result so the operator can
  // see history of the dry-run right above the apply button.
  const [projDryData, setProjDryData] = useState(null);
  const [projApplyData, setProjApplyData] = useState(null);
  const [projLoading, setProjLoading] = useState(false);

  const [mergeFrom, setMergeFrom] = useState('');
  const [mergeTo, setMergeTo] = useState('');
  const [mergeDryData, setMergeDryData] = useState(null);
  const [mergeApplyData, setMergeApplyData] = useState(null);
  const [mergeLoading, setMergeLoading] = useState(false);

  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const [logRows, setLogRows] = useState(null);
  const [logLoading, setLogLoading] = useState(false);

  const run = async (setLoading, action, label, successText) => {
    setLoading(true);
    try {
      const data = await action();
      toast.success(successText || `${label} complete`);
      return data;
    } catch (e) {
      toast.error(`${label} failed: ${e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const projDryRun = () => run(setProjLoading,
    () => callApi('POST', '/api/admin/dedupe/projects'),
    'Dry run',
    'Dry-run complete — review below before applying',
  ).then(d => { if (d) { setProjDryData(d); loadLog(); } });

  const projApply = async () => {
    if (!projDryData) {
      toast.error('Run the dry-run first so you can see what will be deleted.');
      return;
    }
    if (projDryData.would_delete === 0) {
      toast.info('No duplicates to remove.');
      return;
    }
    if (!window.confirm(`Permanently delete ${projDryData.would_delete} duplicate project document(s)? This cannot be undone.`)) return;
    const d = await run(setProjLoading,
      () => callApi('POST', '/api/admin/dedupe/projects?apply=true'),
      'Apply dedupe',
      'Duplicates removed',
    );
    if (d) { setProjApplyData(d); loadLog(); }
  };

  const mergeDryRun = () => {
    if (!mergeFrom.trim() || !mergeTo.trim()) { toast.error('Both customer IDs are required'); return; }
    return run(setMergeLoading,
      () => callApi('POST', `/api/admin/dedupe/merge-customer?from_customer_id=${encodeURIComponent(mergeFrom.trim())}&to_customer_id=${encodeURIComponent(mergeTo.trim())}`),
      'Merge dry-run',
      'Merge preview ready — review before applying',
    ).then(d => { if (d) { setMergeDryData(d); loadLog(); } });
  };

  const mergeApply = async () => {
    if (!mergeDryData) { toast.error('Run the dry-run first.'); return; }
    if (!window.confirm(`Reassign ${mergeDryData.projects_to_reassign} project(s) from ${mergeFrom} to ${mergeTo} and DELETE customer ${mergeFrom}? This cannot be undone.`)) return;
    const d = await run(setMergeLoading,
      () => callApi('POST', `/api/admin/dedupe/merge-customer?from_customer_id=${encodeURIComponent(mergeFrom.trim())}&to_customer_id=${encodeURIComponent(mergeTo.trim())}&apply=true`),
      'Merge',
      'Customer merged',
    );
    if (d) { setMergeApplyData(d); loadLog(); }
  };

  const runAudit = () => run(setAuditLoading,
    () => callApi('GET', '/api/admin/dedupe/customer-audit'),
    'Customer audit',
    'Audit complete',
  ).then(d => { if (d) setAuditData(d); });

  const loadLog = () => run(setLogLoading,
    () => callApi('GET', '/api/admin/dedupe/audit-log?limit=100'),
    'Audit log',
    null,
  ).then(d => { if (d) setLogRows(d.rows || []); });

  return (
    <Layout>
      <div className="p-4 lg:p-8 max-w-5xl">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Dedup Tools</h1>
          <p className="text-sm text-gray-500 mt-2 max-w-3xl">
            Cleanup utilities for legacy import drift. Every destructive
            operation requires a dry-run first and a confirmation
            dialog before it touches the database. Admin only.
          </p>
        </div>

        {/* 1. Project Dedupe */}
        <section className="bg-white rounded shadow p-6 mb-6" data-testid="section-project-dedupe">
          <div className="flex items-center gap-3 mb-3">
            <Trash2 className="w-6 h-6 text-[#5B8DB8]" />
            <h3 className="text-xl text-gray-700 font-light">Deduplicate Projects</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Finds every <code className="text-xs bg-gray-100 px-1">project_id</code> with more than one document and keeps the most recently updated copy. Dry-run shows you the exact plan; apply commits it.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={projDryRun}
              disabled={projLoading}
              className="px-4 py-2 bg-[#5B8DB8] hover:bg-[#3a6a91] text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="proj-dedupe-dry-btn"
            >
              {projLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
              Dry-run
            </button>
            <button
              type="button"
              onClick={projApply}
              disabled={projLoading || !projDryData || projDryData?.would_delete === 0}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="proj-dedupe-apply-btn"
            >
              <AlertTriangle className="w-4 h-4" />
              Apply ({projDryData?.would_delete ?? '?'} to delete)
            </button>
          </div>
          <ResultPanel title="Dry-run plan" data={projDryData} />
          <ResultPanel title="Apply result" data={projApplyData} />
        </section>

        {/* 2. Merge Customer */}
        <section className="bg-white rounded shadow p-6 mb-6" data-testid="section-merge-customer">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-6 h-6 text-[#5B8DB8]" />
            <h3 className="text-xl text-gray-700 font-light">Merge Customer</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Reassigns every project from the <strong>source</strong> customer to the <strong>destination</strong>, then deletes the source. Project-ID collisions are auto-resolved by keeping the destination side.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs text-gray-600 block mb-1">Source customer ID (will be deleted)</label>
              <input
                type="text"
                value={mergeFrom}
                onChange={(e) => setMergeFrom(e.target.value)}
                placeholder="e.g. 37342"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                data-testid="merge-from-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 block mb-1">Destination customer ID (keeps everything)</label>
              <input
                type="text"
                value={mergeTo}
                onChange={(e) => setMergeTo(e.target.value)}
                placeholder="e.g. R26-6590"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                data-testid="merge-to-input"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={mergeDryRun}
              disabled={mergeLoading || !mergeFrom || !mergeTo}
              className="px-4 py-2 bg-[#5B8DB8] hover:bg-[#3a6a91] text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="merge-dry-btn"
            >
              {mergeLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
              Dry-run
            </button>
            <button
              type="button"
              onClick={mergeApply}
              disabled={mergeLoading || !mergeDryData || !mergeDryData?.source_found}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="merge-apply-btn"
            >
              <AlertTriangle className="w-4 h-4" />
              Apply merge
            </button>
          </div>
          <ResultPanel title="Dry-run plan" data={mergeDryData} />
          <ResultPanel title="Apply result" data={mergeApplyData} />
        </section>

        {/* 3. Customer Audit */}
        <section className="bg-white rounded shadow p-6" data-testid="section-customer-audit">
          <div className="flex items-center gap-3 mb-3">
            <FileSearch className="w-6 h-6 text-[#5B8DB8]" />
            <h3 className="text-xl text-gray-700 font-light">Customer Audit</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Read-only scan that groups customers by last name + first name + first address. Clusters with 2+ members are candidates for the Merge tool above. Nothing is mutated.
          </p>
          <button
            type="button"
            onClick={runAudit}
            disabled={auditLoading}
            className="px-4 py-2 bg-[#5B8DB8] hover:bg-[#3a6a91] text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="audit-run-btn"
          >
            {auditLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
            Run audit
          </button>
          {auditData && (
            <div className="mt-4 space-y-3">
              <div className="text-sm text-gray-700">
                <strong>{auditData.duplicate_customer_clusters}</strong> duplicate cluster(s) found ·{' '}
                <strong>{auditData.total_duplicate_records}</strong> total records involved
              </div>
              <div className="border border-gray-200 rounded max-h-[500px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-gray-600 font-normal">Name</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Address</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Customer IDs</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Type</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Phone / Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditData.clusters || []).map((cluster, ci) => (
                      cluster.members.map((m, mi) => (
                        <tr key={`${ci}-${mi}`} className={`border-b ${mi === 0 ? 'border-t-2 border-t-gray-300' : ''} hover:bg-gray-50`}>
                          {mi === 0 ? <td className="p-2 text-gray-700 align-top" rowSpan={cluster.members.length}>{m.name}</td> : null}
                          {mi === 0 ? <td className="p-2 text-gray-500 align-top font-mono text-[10px]" rowSpan={cluster.members.length}>{m.address_signature}</td> : null}
                          <td className="p-2 font-mono">
                            <button
                              type="button"
                              onClick={() => { if (!mergeFrom) setMergeFrom(m.customer_id); else if (!mergeTo) setMergeTo(m.customer_id); }}
                              className="text-[#5B8DB8] hover:underline"
                              title="Click to copy into the Merge form above"
                              data-testid={`audit-cust-${m.customer_id}`}
                            >
                              {m.customer_id}
                            </button>
                          </td>
                          <td className="p-2 text-gray-600">{m.type || '—'}</td>
                          <td className="p-2 text-gray-600">
                            {m.primary_phone && <div>{m.primary_phone}</div>}
                            {m.email && <div className="text-gray-400">{m.email}</div>}
                          </td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">
                Tip: click a customer ID above to populate the Merge form (first click fills Source, second click fills Destination).
              </p>
            </div>
          )}
        </section>

        {/* 4. Audit Log */}
        <section className="bg-white rounded shadow p-6 mt-6" data-testid="section-audit-log">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <History className="w-6 h-6 text-[#5B8DB8]" />
              <h3 className="text-xl text-gray-700 font-light">Recent Activity</h3>
            </div>
            <button
              type="button"
              onClick={loadLog}
              disabled={logLoading}
              className="text-sm text-[#5B8DB8] hover:text-[#3a6a91] flex items-center gap-1 disabled:opacity-50"
              data-testid="audit-log-refresh"
            >
              {logLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Append-only log of every dedup action (dry-runs and applies). The list auto-refreshes after each action above.
          </p>
          {logRows === null ? (
            <div className="text-sm text-gray-400 italic">Click <strong>Refresh</strong> to load the log.</div>
          ) : logRows.length === 0 ? (
            <div className="text-sm text-gray-400 italic">No activity recorded yet.</div>
          ) : (
            <div className="border border-gray-200 rounded max-h-[500px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-gray-600 font-normal">When</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Operator</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Action</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Mode</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Params</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50 align-top">
                      <td className="p-2 text-gray-500 whitespace-nowrap font-mono text-[10px]">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="p-2 text-gray-700">
                        {r.operator_name || r.operator_email || '—'}
                      </td>
                      <td className="p-2 text-gray-700 font-mono">{r.action}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${r.applied ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                          {r.applied ? 'APPLIED' : 'dry-run'}
                        </span>
                      </td>
                      <td className="p-2 text-gray-500 font-mono text-[10px]">
                        {r.params && Object.keys(r.params).length > 0 ? JSON.stringify(r.params) : '—'}
                      </td>
                      <td className="p-2 text-gray-500 font-mono text-[10px]">
                        {r.result_summary && Object.keys(r.result_summary).length > 0 ? JSON.stringify(r.result_summary) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default DedupTools;
